import type { FC } from 'react';
import { Box, Button, Checkbox, Group, TextInput, Text } from '@mantine/core';
import { useFormik } from 'formik';
import { PhoneInput } from '@newo/ui-kit';
import { useDisclosure } from '@mantine/hooks';
import styles from './PartnerOrganizationForm.module.css';
import validationSchema from './PartnerOrganizationForm.scheme';
import { useCreatePartnerOrganization } from '@/services/customer/customer/customer.queries.ts';
import useAppStore from '@/store/app.ts';
import useQueryParams from '@/hooks/useQueryParams.ts';

type PartnerOrganizationFormProps = {
    handleBack: () => void;
    handleNext: () => void;
};

const CLIENT_SECRET = import.meta.env.VITE_CLIENT_SECRET;

const PartnerOrganizationForm: FC<PartnerOrganizationFormProps> = ({
    handleBack
}) => {
    const externalId = useAppStore((state) => state.externalId);
    const [isPending, { open }] = useDisclosure(false);
    const { mutateAsync } = useCreatePartnerOrganization();
    const params = useQueryParams();
    const formik = useFormik({
        initialValues: {
            fullname: '',
            email: '',
            phone: '',
            organisation_name: '',
            organisation_website: '',
            contact_consent: false,
            agree_terms: false
        },
        validationSchema,
        validateOnChange: true,
        onSubmit: async ({ agree_terms, ...formData }) => {
            await mutateAsync({
                customer: {
                    ...formData,
                    contact_consent: JSON.stringify(formData.contact_consent),
                    query_params: JSON.stringify(params)
                },
                externalId,
                client_secret: CLIENT_SECRET
            });
            open();
        }
    });

    const handlePhoneChange = async (value: string) => {
        if (formik.values.phone !== value) {
            formik.setFieldTouched('phone', true);
            formik.setFieldValue('phone', value);
        }
    };

    const handleChangePhoneNumberValidity = (isValid: boolean) => {
        const phone = formik.values.phone.trim();

        let nextError: string | undefined;

        if (phone && !isValid) {
            nextError = 'Invalid phone number format';
        }

        if (formik.errors.phone !== nextError) {
            formik.setFieldError('phone', nextError);
        }
    };

    return (
        <form
            noValidate
            className={styles.partnerOrganizationForm}
            onSubmit={formik.handleSubmit}
        >
            <Box className={styles.partnerOrganizationFormBody}>
                <TextInput
                    required
                    error={
                        formik.touched.fullname
                            ? formik.errors.fullname
                            : undefined
                    }
                    label="Full Name"
                    name="fullname"
                    placeholder="Your Name"
                    value={formik.values.fullname}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                />

                <TextInput
                    required
                    error={
                        formik.touched.email ? formik.errors.email : undefined
                    }
                    label="Email"
                    name="email"
                    placeholder="Your Email"
                    value={formik.values.email}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                />

                <PhoneInput
                    error={
                        formik.touched.phone ? formik.errors.phone : undefined
                    }
                    value={formik.values.phone}
                    onChange={handlePhoneChange}
                    onChangeValidity={handleChangePhoneNumberValidity}
                />

                <TextInput
                    required
                    error={
                        formik.touched.organisation_name
                            ? formik.errors.organisation_name
                            : undefined
                    }
                    label="Your Organization Name"
                    name="organisation_name"
                    placeholder="Your Organization Name"
                    value={formik.values.organisation_name}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                />

                <TextInput
                    error={formik.errors.organisation_website ?? undefined}
                    label="Your Organization Website"
                    name="organisation_website"
                    placeholder="Your Organization Website"
                    value={formik.values.organisation_website}
                    onChange={formik.handleChange}
                />

                <Box>
                    <Checkbox
                        required
                        checked={formik.values.contact_consent}
                        label="I agree to be contacted for AI guided onboarding."
                        name="contact_consent"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />

                    <Checkbox
                        required
                        checked={formik.values.agree_terms}
                        label={
                            <Text fz={13}>
                                I agree with{' '}
                                <Text
                                    inherit
                                    className="underline"
                                    component="a"
                                    href="https://newo.ai/terms-of-use"
                                    target="_blank"
                                >
                                    Terms of Use
                                </Text>
                                <Text span c="red" ml={2}>
                                    *
                                </Text>
                            </Text>
                        }
                        mt="sm"
                        name="agree_terms"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                </Box>
            </Box>

            <Group justify="space-between" mt="lg" w="100%">
                <Button p={0} variant="transparent" onClick={handleBack}>
                    Back
                </Button>

                <Button
                    disabled={
                        !formik.isValid || formik.isSubmitting || !formik.dirty
                    }
                    loading={isPending || formik.isSubmitting}
                    px={91}
                    type="submit"
                >
                    Create
                </Button>
            </Group>
        </form>
    );
};

export default PartnerOrganizationForm;
